/**
 * Payment Success Page — ProfitSpot
 * Beautiful confirmation page after successful Stripe payment
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check, Sparkles, Crown, Zap, Shield, Brain, Bell, BarChart3, Download, ArrowRight } from 'lucide-react';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

const FeatureItem = ({ icon: Icon, text, color = 'text-[#00ff88]' }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
    <div className={`w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
    <span className="text-sm text-gray-300">{text}</span>
  </div>
);

const PRO_FEATURES = [
  { icon: Shield, text: '500 yield pools with security scores', color: 'text-[#00ff88]' },
  { icon: BarChart3, text: 'Full Simulator Labs & Break-even charts', color: 'text-[#00d4ff]' },
  { icon: Zap, text: 'Portfolio Tracker with live data', color: 'text-[#ffa502]' },
  { icon: Bell, text: '3 pool alerts & email notifications', color: 'text-purple-400' },
];

const ELITE_FEATURES = [
  { icon: Crown, text: 'Unlimited yield pools — no limits', color: 'text-[#ffa502]' },
  { icon: Brain, text: 'AI Pool Insights & Whale Tracker', color: 'text-[#00d4ff]' },
  { icon: Sparkles, text: 'Forecast Dial & Advanced Backtesting', color: 'text-[#00ff88]' },
  { icon: Bell, text: 'Telegram + unlimited alerts', color: 'text-purple-400' },
  { icon: Download, text: 'CSV data export', color: 'text-pink-400' },
];

/**
 * Fire Google Ads conversion event after successful payment.
 * Global gtag.js is loaded in index.html (AW-18057240871).
 */
function trackGoogleAdsConversion(tier) {
  if (typeof window.gtag === 'function') {
    const value = tier === 'elite' || tier === 'unlimited' ? 79.0 : 29.0;
    window.gtag('event', 'conversion', {
      send_to: 'AW-18057240871/LgUPCP657pMcEKfCrqJD',
      value: value,
      currency: 'USD',
    });
  }
}

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tierInfo, setTierInfo] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Refresh user data to get updated tier
        await refreshUser();
        
        // Optionally verify session with backend
        if (sessionId) {
          const res = await fetch(`${API_URL}/api/payments/verify-session?session_id=${sessionId}`);
          if (res.ok) {
            const data = await res.json();
            setTierInfo(data);

            // Track Google Ads conversion on successful payment verification
            trackGoogleAdsConversion(data.tier);
          }
        }
      } catch (e) {
        console.error('Error verifying payment:', e);
      }
      setLoading(false);
    };

    verifyPayment();

    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  // Determine tier from user data or tierInfo
  const tier = tierInfo?.tier || user?.subscription?.tier || 'pro';
  const isElite = tier === 'elite' || tier === 'unlimited';
  const tierName = isElite ? 'Elite' : 'Pro';
  const features = isElite ? ELITE_FEATURES : PRO_FEATURES;

  return (
    <div className="min-h-screen bg-[#0a0a12] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00ff88]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none z-10">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-20px',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  backgroundColor: ['#00ff88', '#00d4ff', '#ffa502', '#ff6b6b', '#a855f7'][Math.floor(Math.random() * 5)],
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Main Card */}
      <div className="relative z-20 w-full max-w-md">
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with Checkmark */}
          <div className="bg-gradient-to-b from-[#00ff88]/20 to-transparent p-8 text-center">
            {/* Animated Checkmark */}
            <div className="relative inline-block mb-6">
              <div className="w-20 h-20 rounded-full bg-[#00ff88]/20 border-2 border-[#00ff88] flex items-center justify-center animate-scale-in">
                <Check className="w-10 h-10 text-[#00ff88] animate-check-draw" strokeWidth={3} />
              </div>
              {/* Ring animation */}
              <div className="absolute inset-0 rounded-full border-2 border-[#00ff88] animate-ping opacity-30" />
            </div>

            {/* Welcome Message */}
            <h1 className="text-2xl font-bold text-white mb-2">
              Welcome to {tierName}! 🎉
            </h1>
            <p className="text-gray-400 text-sm">
              Your account has been upgraded successfully
            </p>
          </div>

          {/* Features List */}
          <div className="p-6 space-y-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#ffa502]" />
              Unlocked Features
            </p>
            {features.map((feature, i) => (
              <FeatureItem key={i} {...feature} />
            ))}
          </div>

          {/* CTA Button */}
          <div className="p-6 pt-0">
            <button
              onClick={() => navigate('/explore')}
              className="w-full py-4 bg-[#00ff88] text-black font-bold rounded-xl hover:bg-[#00cc6a] transition-all flex items-center justify-center gap-2 text-lg group"
              data-testid="start-exploring-btn"
            >
              Start Exploring
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-center text-xs text-gray-600 mt-3">
              Your subscription is now active • <a href="/alert-preferences" className="text-[#00d4ff] hover:underline">Set up alerts</a>
            </p>
          </div>
        </div>

        {/* Support Link */}
        <p className="text-center text-xs text-gray-600 mt-4">
          Questions? <a href="mailto:support@profitspot.live" className="text-[#00d4ff] hover:underline">Contact support</a>
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
        @keyframes scale-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.5s ease-out forwards;
        }
        @keyframes check-draw {
          0% {
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
        .animate-check-draw {
          stroke-dasharray: 100;
          animation: check-draw 0.5s ease-out 0.3s forwards;
        }
      `}</style>
    </div>
  );
}
